import { useMemo, type ReactNode } from "react";
import { createStorageService } from "../shared/api/storage";
import { localSupabaseClient, type TypedSupabaseClient } from "../shared/api/supabase";
import { createTelemetry } from "../shared/api/telemetry";
import { PublicAppError } from "../shared/lib/errors";
import { useSession } from "../features/auth/SessionProvider";
import { createBodyApi, type BodyApi } from "../features/body/api/body-api";
import { BodyApiProvider } from "../features/body/api/queries";
import { createExamsApi, type ExamsApi } from "../features/exams/api/exams-api";
import { ExamsApiProvider } from "../features/exams/api/queries";
import { createNutritionApi, type NutritionApi } from "../features/nutrition/api/nutrition-api";
import { NutritionApiProvider } from "../features/nutrition/api/queries";
import { createProtocolsApi, type ProtocolsApi } from "../features/protocols/api/protocols-api";
import { ProtocolsApiProvider } from "../features/protocols/api/queries";
import { createTrainingApi, type TrainingApi } from "../features/training/api/training-api";
import { TrainingApiProvider } from "../features/training/api/queries";
import { createReportsApi, type ReportsApi } from "../features/reports/api/reports-api";
import { ReportsApiProvider } from "../features/reports/api/context";

export interface DomainApis {
  protocols: ProtocolsApi;
  exams: ExamsApi;
  body: BodyApi;
  training: TrainingApi;
  nutrition: NutritionApi;
  reports: ReportsApi;
}

interface DomainProvidersProps {
  children: ReactNode;
  client?: TypedSupabaseClient | null;
  apis?: Partial<DomainApis>;
}

/**
 * Instancia uma vez o serviço central de Storage privado, a telemetria da
 * conta e as cinco APIs de domínio. As features só se enxergam por estes
 * contratos; nenhuma delas cria cliente Supabase por conta própria.
 */
export function DomainProviders({
  children,
  client = localSupabaseClient,
  apis,
}: DomainProvidersProps) {
  const { user } = useSession();
  const userId = user?.id ?? "";
  const value = useMemo<DomainApis | null>(() => {
    if (client === null) return null;
    const telemetry = createTelemetry(client, userId);
    const storage = createStorageService(client, (event) => {
      telemetry.record({
        eventType: event.errorCode === undefined ? "storage" : "error",
        action: event.action,
        domain: event.domain,
        storageDeltaBytes: event.storageDeltaBytes ?? null,
        errorCode: event.errorCode ?? null,
      });
    });
    return {
      protocols: apis?.protocols ?? createProtocolsApi(client),
      exams: apis?.exams ?? createExamsApi(client, storage),
      body: apis?.body ?? createBodyApi(client, storage),
      training: apis?.training ?? createTrainingApi(client),
      nutrition: apis?.nutrition ?? createNutritionApi(client),
      reports: apis?.reports ?? createReportsApi(client, storage),
    };
  }, [client, userId, apis]);

  if (value === null) {
    throw new PublicAppError(
      "missing_public_config",
      "A configuração pública da aplicação não está disponível.",
    );
  }
  return (
    <ProtocolsApiProvider value={value.protocols}>
      <ExamsApiProvider value={value.exams}>
        <BodyApiProvider value={value.body}>
          <TrainingApiProvider value={value.training}>
            <NutritionApiProvider value={value.nutrition}>
              <ReportsApiProvider value={value.reports}>{children}</ReportsApiProvider>
            </NutritionApiProvider>
          </TrainingApiProvider>
        </BodyApiProvider>
      </ExamsApiProvider>
    </ProtocolsApiProvider>
  );
}
